import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ToastService } from 'angular-toastify';
import { InvoiceDetailPayload, InvoiceFeeList } from '../../../../../../core/models/invoice.model';
import { CommonModule } from '@angular/common';
import { VatSettingService } from '../../../../../../core/services/vat-setting.service';
import { addVatToExclusive, extractVatFromInclusive, formatVatRate, roundMoney } from '../../../../../../core/utils/vat-amount.util';

/** inclusive = ยอดที่กรอกรวม VAT แล้ว · exclusive = ยอดก่อน VAT (ระบบบวก VAT ให้) */
export type InvoiceAmountMode = 'inclusive' | 'exclusive';

@Component({
    selector: 'app-modal-invoice-add-detail',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './modal-invoice-add-detail.component.html',
    styleUrls: ['./modal-invoice-add-detail.component.css']
})
export class ModalInvoiceAddDetailComponent implements OnInit {
    @Input() feeList: InvoiceFeeList[] = [];
    @Input() editItem: InvoiceDetailPayload | null = null;
    @Output() close = new EventEmitter<void>();
    @Output() save = new EventEmitter<InvoiceDetailPayload>();

    newItemDescription: string = '';
    newItemFeeId: number | null = null;
    newItemQuantity: number = 1;
    newItemAmount: number = 0;

    // Search and Dropdown State
    searchFeeText: string = '';
    filteredFeeList: InvoiceFeeList[] = [];
    isDropdownOpen: boolean = false;

    private readonly vatSetting = inject(VatSettingService);

    constructor(private toast: ToastService) { }

    ngOnInit(): void {
        this.filteredFeeList = this.feeList;
        if (this.editItem) {
            this.newItemDescription = this.editItem.description;
            this.newItemFeeId = this.editItem.invoiceFeeId;
            this.newItemQuantity = this.editItem.quantity;
            this.newItemAmount = this.getEditItemUnitTotal();
        }
    }

    // --- Searchable Dropdown Logic ---

    toggleDropdown(event: Event) {
        event.stopPropagation();
        this.isDropdownOpen = !this.isDropdownOpen;
        if (this.isDropdownOpen) {
            this.searchFeeText = '';
            this.filteredFeeList = this.feeList;
        }
    }

    closeDropdown() {
        this.isDropdownOpen = false;
    }

    filterFees() {
        if (!this.searchFeeText) {
            this.filteredFeeList = this.feeList;
        } else {
            const search = this.searchFeeText.toLowerCase();
            this.filteredFeeList = this.feeList.filter(f =>
                f.name.toLowerCase().includes(search)
            );
        }
    }

    selectFee(fee: InvoiceFeeList) {
        this.newItemFeeId = fee.id;
        if (!this.newItemDescription) {
            this.newItemDescription = fee.name;
        }
        this.closeDropdown();
    }

    get selectedFeeName(): string {
        if (!this.newItemFeeId) return '';
        return this.feeList.find(f => f.id === this.newItemFeeId)?.name || '';
    }

    onFeeItemChange() {
        if (this.newItemFeeId) {
            const selectedFee = this.feeList.find(f => f.id == this.newItemFeeId);
            // Optional: Auto-fill description if empty
            if (selectedFee && !this.newItemDescription) {
                this.newItemDescription = selectedFee.name;
            }
        }
    }

    saveItem() {
        if (!this.newItemDescription || !this.newItemFeeId || this.newItemAmount <= 0) {
            this.toast.error('กรุณากรอกข้อมูลรายการให้ครบถ้วน');
            return;
        }

        const selectedFee = this.feeList.find((fee) => fee.id === this.newItemFeeId);
        // API รับ amount = ราคาต่อหน่วยก่อน VAT แล้วคิด VAT เอง (VatAmountHelper.FromExclusive)
        const { unitExclusive, unitVat } = this.vatPreview;
        const newItem: InvoiceDetailPayload = {
            id: this.editItem?.id ?? 0,
            description: this.newItemDescription,
            feeId: selectedFee?.feeId ?? this.editItem?.feeId ?? 0,
            invoiceFeeId: this.newItemFeeId,
            quantity: this.newItemQuantity,
            amount: unitExclusive,
            vat: selectedFee?.vat ? unitVat : undefined,
        };
        //console.log("newItem: ", newItem);
        this.save.emit(newItem);
        this.resetForm();
    }

    resetForm() {
        this.newItemDescription = '';
        this.newItemFeeId = null;
        this.newItemQuantity = 1;
        this.newItemAmount = 0;
        this.amountMode = 'inclusive';
    }

    onClose() {
        this.close.emit();
    }

    /** แสดงราคาต่อหน่วยรวม VAT ในช่อง input (amount จาก API/ฟอร์ม = ก่อน VAT) */
    private getEditItemUnitTotal(): number {
        if (!this.editItem) {
            return 0;
        }
        const fee = this.feeList.find((f) => f.id === this.editItem!.invoiceFeeId);
        if (!fee?.vat) {
            return this.editItem.amount;
        }
        const unitVat = this.editItem.vat != null
            ? this.editItem.vat
            : addVatToExclusive(this.editItem.amount, this.vatSetting.ratePercent()).vatAmount;
        return Math.round((this.editItem.amount + unitVat) * 100) / 100;
    }

    // --- VAT: เลือกว่ายอดที่กรอกรวม VAT แล้ว หรือยังไม่รวม ---
    amountMode: InvoiceAmountMode = 'inclusive';

    get selectedFeeHasVat(): boolean {
        if (!this.newItemFeeId) return false;
        return !!this.feeList.find(f => f.id === this.newItemFeeId)?.vat;
    }

    get vatRateLabel(): string {
        return formatVatRate(this.vatSetting.ratePercent());
    }

    setAmountMode(mode: InvoiceAmountMode): void {
        this.amountMode = mode;
    }

    /** ตัวเลขเดียวกับที่ API จะบันทึก: fee = ก่อน VAT ต่อหน่วย, vat = FromExclusive(fee), total = round((fee+vat)×qty) */
    get vatPreview(): {
        unitExclusive: number;
        unitVat: number;
        unitTotal: number;
        lineExclusive: number;
        lineVat: number;
        lineTotal: number;
        roundingDiff: number;
    } {
        const qty = Math.max(this.newItemQuantity || 0, 0);
        const entered = roundMoney(this.newItemAmount || 0);
        const ratePercent = this.vatSetting.ratePercent();

        let unitExclusive = entered;
        let unitVat = 0;
        if (this.selectedFeeHasVat) {
            unitExclusive = this.amountMode === 'inclusive'
                ? extractVatFromInclusive(entered, ratePercent).subTotal
                : entered;
            unitVat = addVatToExclusive(unitExclusive, ratePercent).vatAmount;
        }
        const unitTotal = roundMoney(unitExclusive + unitVat);
        const lineTotal = roundMoney(unitTotal * qty);
        const lineExclusive = roundMoney(unitExclusive * qty);
        const lineVat = roundMoney(lineTotal - lineExclusive);
        const roundingDiff = this.selectedFeeHasVat && this.amountMode === 'inclusive'
            ? roundMoney(unitTotal - entered)
            : 0;

        return { unitExclusive, unitVat, unitTotal, lineExclusive, lineVat, lineTotal, roundingDiff };
    }
}
